import * as React from 'react';
import * as styles from "./Accordion.css";
import { IAccordion } from '../../interfaces/IAccordion';
import { IAIcon } from "../icon/Icon";
import { IATooltip } from '../tooltip/Tooltip';
import "../../styles/BasicIA.css";
import { IFontSlots } from '../../interfaces/IFontSlots';

let instanceCounter = 0;

export interface IAccordionProps {
  items: IAccordion[];
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  collapseOthers?: boolean;
  style?: React.CSSProperties;
  fontSlots?: IFontSlots;
  onChange?: (items: IAccordion[]) => void;
}

export class IAAccordion extends React.Component<IAccordionProps> {
  private instanceId: string;
  private containerRef = React.createRef<HTMLDivElement>();

  constructor(props: IAccordionProps) {
    super(props);
    this.instanceId = `ia-accordion-${++instanceCounter}`;
  }

  private onClick(i: number) {
    let items = this.props.items;
    items[i].expanded = !items[i].expanded;
    // if expanding, collapse the rest
    if (items[i].expanded == true && (this.props.collapseOthers === undefined || this.props.collapseOthers)) {
      items.forEach((item, index) => {
        if (index != i) {
          item.expanded = false;
        }
      });
    }
    this.props.onChange(items);
  }

  private onKeyDown(e: React.KeyboardEvent, i: number) {
    const buttons = this.containerRef.current?.querySelectorAll<HTMLButtonElement>('button[aria-expanded]');
    if (!buttons || buttons.length === 0) return;
    const arr = Array.from(buttons);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        arr[(i + 1) % arr.length]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        arr[(i - 1 + arr.length) % arr.length]?.focus();
        break;
      case 'Home':
        e.preventDefault();
        arr[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        arr[arr.length - 1]?.focus();
        break;
    }
  }

  public render(): JSX.Element {
    let accordionElements: JSX.Element[] = [];
    if (this.props.items != undefined) {
      this.props.items.forEach((item, i) => {
        const headerId = `${this.instanceId}-header-${i}`;
        const panelId = `${this.instanceId}-panel-${i}`;
        accordionElements.push(
          <div
            role="heading"
            aria-level={3}
            key={i}
          >
            <button
              id={headerId}
              className={[styles.accordionItemHeadline, "IA_OnFocus"].join(" ")}
              style={{
                color: this.props.color ? this.props.color : "#333333",
                backgroundColor: this.props.backgroundColor ? this.props.backgroundColor : "#f4f4f4",
                borderColor: this.props.borderColor ? this.props.borderColor : "transparent",
                clear: "both"
              }}
              onClick={() => this.onClick(i)}
              onKeyDown={(e) => this.onKeyDown(e, i)}
              aria-expanded={!!item.expanded}
              aria-controls={panelId}
            >
              <div className={`${styles.icon} ${item.expanded ? styles.expanded : ''}`} aria-hidden={true}>
                <IAIcon
                  url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Right/SVG/ic_fluent_chevron_right_48_regular.svg"
                  size={12}
                  color="#333333"
                  style={{ marginTop: 3 }}
                />
              </div>
              <div
                className={styles.accordionItemTitle}
                style={{
                  fontFamily: this.props.fontSlots?.heading?.fontFamily
                }}
              >
                <div
                  style={{
                    float: "left"
                  }}
                >
                  {item != undefined && item.title != undefined ? item.title : ""}
                </div>
                {!!item.toolTip &&
                  <IATooltip
                    content="Tooltip"
                    type={"bubble"}
                    styles={{
                      width: 200,
                      textAlign: "left",
                      color: "#333333",
                      whiteSpace: "break-spaces",
                      float: "left",
                      display: "inline",
                    }}
                    rootStyles={{
                      float: "left",
                      display: "inline",
                    }}
                    componentContent={item.toolTip}
                  >
                    <IAIcon
                      title="Info"
                      size={14}
                      color={this.props.color}
                      style={{
                        cursor: "pointer",
                        display: "inline",
                        marginLeft: 7,
                        float: "right"
                      }}
                    />
                  </IATooltip>
                }
              </div>
            </button>
            {typeof item.content === 'string' || item.content instanceof String ?
              <div
                id={panelId}
                role="region"
                aria-labelledby={headerId}
                aria-hidden={!item.expanded || undefined}
                className={`${styles.accordionItemContent} ${item.expanded ? styles.expanded : styles.collapsed}`}
              >
                <div style={{ margin: 10, fontFamily: this.props.fontSlots?.body?.fontFamily }} dangerouslySetInnerHTML={{ __html: item.content != undefined ? item.content as string : "" }} />
              </div>
              :
              <div
                id={panelId}
                role="region"
                aria-labelledby={headerId}
                aria-hidden={!item.expanded || undefined}
                className={`${styles.accordionItemContent} ${item.expanded ? styles.expanded : styles.collapsed}`}
              >
                <div style={{ margin: 10, fontFamily: this.props.fontSlots?.body?.fontFamily }}>
                  {item.content}
                </div>
              </div>
            }
          </div>
        );
      });
    }
    return (
      <div ref={this.containerRef} className={styles.accordion} style={this.props.style}>
        {accordionElements.length > 0 &&
          accordionElements
        }
      </div>
    );
  }
}